import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Grid,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material'
import { selectUserIsAuthenticated, selectUserDisplayName, selectUserPhotoURL } from '../store/slices/userSlice'
import { waitForDeleteConfirmation } from '../store/slices/notificationsSlice'
import { useEntriesQuery, useUpdateEntryMutation, useToggleFavoriteMutation, useToggleFoundationMutation } from '../hooks/useFirebaseQueries'
import {
  EntryHeader,
  EntryOverviewTab,
  EntryDetailsTab,
  WeightTrackingTab,
  GeneticsTab,
  NotesTab,
  EntrySidebar,
  WeightDialog,
  BreedingCalculatorDialog
} from '../components/EntryDetails'
import EditWeightDialog from '../components/EntryDetails/EditWeightDialog'
import { useWeightTracking } from '../hooks/EntryDetails/useWeightTracking'
import { useGeneticsCalculator } from '../hooks/EntryDetails/useGeneticsCalculator'
import Gallery from '../components/EntryDetails/Gallery'

const EntryDetailsPage = () => {
  const { id: entryId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [currentTab, setCurrentTab] = useState(0)
  const [editMode, setEditMode] = useState(false)
  const [weightDialogOpen, setWeightDialogOpen] = useState(false)
  const [editWeightDialogOpen, setEditWeightDialogOpen] = useState(false)
  const [editingWeightIndex, setEditingWeightIndex] = useState(null)
  const [breedingCalculatorOpen, setBreedingCalculatorOpen] = useState(false)
  
  const isAuthenticated = useSelector(selectUserIsAuthenticated)
  const userDisplayName = useSelector(selectUserDisplayName)
  const userPhotoURL = useSelector(selectUserPhotoURL)
  
  const { data: entries = [], isLoading } = useEntriesQuery()
  const updateEntryMutation = useUpdateEntryMutation()
  const toggleFavoriteMutation = useToggleFavoriteMutation()
  const toggleFoundationMutation = useToggleFoundationMutation()

  const entry = entries.find(e => e.entryId === entryId)
  
  // Custom hooks for weight tracking and genetics
  const weightTracking = useWeightTracking(entry, userDisplayName, userPhotoURL, updateEntryMutation)
  const genetics = useGeneticsCalculator(entries)

  const form = useForm({
    defaultValues: {
      name: '',
      breed: '',
      variety: '',
      sex: '',
      species: '',
      phase: '',
      hatchDate: '',
      characteristics: [],
      notes: {
        active: '',
        archived: []
      },
      isFavorited: false,
      isFoundation: false,
      genetics: {}
    }
  })


  // Populate form with entry data when entry is loaded or edit mode is enabled
  useEffect(() => {
    if (entry && editMode) {
      form.reset({
        name: entry.name || '',
        breed: entry.breed || '',
        variety: entry.variety || '',
        sex: entry.sex || '',
        species: entry.species || '',
        phase: entry.phase || '',
        hatchDate: entry.hatchDate || '',
        characteristics: entry.characteristics || [],
        notes: entry.notes || {
          active: '',
          archived: []
        },
        isFavorited: entry.isFavorited || false,
        isFoundation: entry.isFoundation || false,
        genetics: entry.genetics || {}
      })
    }
  }, [entry, editMode, form])

  const handleToggleFavorite = () => {
    toggleFavoriteMutation.mutate({
      entryId: entry.entryId,
      isFavorite: entry.isFavorited
    })
  }

  const handleToggleFoundation = () => {
    toggleFoundationMutation.mutate({
      entryId: entry.entryId,
      isFoundation: entry.isFoundation
    })
  }

  const handleSaveChanges = async () => {
    const formData = form.getValues()
    try {
      await updateEntryMutation.mutateAsync({
        entryId,
        entryData: formData,
        userDisplayName,
        userPhotoURL
      })
      setEditMode(false)
    } catch (error) {
      console.error('Error updating entry:', error)
    }
  }

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue)
  }

  const handleEditWeight = (weightIndex) => {
    setEditingWeightIndex(weightIndex)
    setEditWeightDialogOpen(true)
  }

  const handleDeleteWeight = async (weightIndex) => {
    const confirmed = await dispatch(waitForDeleteConfirmation())
    if (confirmed) {
      await weightTracking.handleDeleteWeight(entryId, weightIndex)
    }
  }

  const handleSaveEditedWeight = async (updatedWeightData) => {
    await weightTracking.handleEditWeight(entryId, editingWeightIndex, updatedWeightData, () => {
      setEditWeightDialogOpen(false)
      setEditingWeightIndex(null)
    })
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <Alert severity="warning">
          Please log in to view entry details.
        </Alert>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!entry) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Entry not found. Please check the URL and try again.
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <EntryHeader 
        entry={entry}
        editMode={editMode}
        setEditMode={setEditMode}
        form={form}
        handleSaveChanges={handleSaveChanges}
        handleToggleFavorite={handleToggleFavorite}
        handleToggleFoundation={handleToggleFoundation}
        updateEntryMutation={updateEntryMutation}
      />

      {!editMode && (
        <Grid container spacing={3}>
          {/* Sidebar - shows first on mobile */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ order: { xs: 1, md: 2 } }}>
            <EntrySidebar 
              entry={entry}
              displayUnit={weightTracking.displayUnit}
              formatWeight={weightTracking.formatWeight}
              getUnitLabel={weightTracking.getUnitLabel}
              handleToggleFavorite={handleToggleFavorite}
              handleToggleFoundation={handleToggleFoundation}
              onAddWeight={() => setWeightDialogOpen(true)}
            />
          </Grid>

          {/* Main Content - shows second on mobile */}
          <Grid size={{ xs: 12, md: 8 }} sx={{ order: { xs: 2, md: 1 } }}>
            <Paper sx={{ mb: 3 }}>
              <Tabs 
                value={currentTab} 
                onChange={handleTabChange} 
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Overview" />
                <Tab label="Details" />
                <Tab label="Weights" />
                <Tab label="Genetics" />
                <Tab label="Notes" />
                <Tab label="History" />
                <Tab label ="Gallery" />
              </Tabs>

              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {currentTab === 0 && (
                  <EntryOverviewTab entry={entry} />
                )}

                {currentTab === 1 && (
                  <EntryDetailsTab entry={entry} />
                )}

                {currentTab === 2 && (
                  <WeightTrackingTab 
                    entry={entry}
                    displayUnit={weightTracking.displayUnit}
                    setDisplayUnit={weightTracking.setDisplayUnit}
                    chartTimePeriod={weightTracking.chartTimePeriod}
                    setChartTimePeriod={weightTracking.setChartTimePeriod}
                    onAddWeight={() => setWeightDialogOpen(true)}
                    onEditWeight={handleEditWeight}
                    onDeleteWeight={handleDeleteWeight}
                    formatWeight={weightTracking.formatWeight}
                    getUnitLabel={weightTracking.getUnitLabel}
                    getChartData={weightTracking.getChartData}
                    calculateGrowthRate={weightTracking.calculateGrowthRate}
                  />
                )}

                {currentTab === 3 && (
                  <GeneticsTab 
                    entry={entry}
                    setEditMode={setEditMode}
                    onTestBreeding={() => setBreedingCalculatorOpen(true)}
                    breedingResults={genetics.breedingResults}
                    setBreedingResults={genetics.setBreedingResults}
                    chickenGenes={genetics.chickenGenes}
                    getGeneticProfile={genetics.getGeneticProfile}
                    getELocusPhenotype={genetics.getELocusPhenotype}
                    getPhenotype={genetics.getPhenotype}
                  />
                )}

                {currentTab === 4 && (
                  <NotesTab entry={entry} />
                )}

                {currentTab === 5 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Entry History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Historical data and lineage information will be displayed here.
                    </Typography>
                  </Box>
                )}
                {currentTab === 6 && (
                  <Gallery entry={entry} />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      <BreedingCalculatorDialog 
        open={breedingCalculatorOpen}
        onClose={() => setBreedingCalculatorOpen(false)}
        entry={entry}
        entryId={entryId}
        entries={entries}
        selectedMate={genetics.selectedMate}
        setSelectedMate={genetics.setSelectedMate}
        breedingResults={genetics.breedingResults}
        onCalculateBreeding={() => genetics.handleBreedingCalculation(entry)}
      />

      <WeightDialog 
        open={weightDialogOpen}
        onClose={() => setWeightDialogOpen(false)}
        entry={entry}
        newWeight={weightTracking.newWeight}
        setNewWeight={weightTracking.setNewWeight}
        weightDate={weightTracking.weightDate}
        setWeightDate={weightTracking.setWeightDate}
        weightUnit={weightTracking.weightUnit}
        setWeightUnit={weightTracking.setWeightUnit}
        onAddWeight={() => weightTracking.handleAddWeight(entryId, () => setWeightDialogOpen(false))}
        getUnitLabel={weightTracking.getUnitLabel}
        updateEntryMutation={updateEntryMutation}
      />

      <EditWeightDialog
        open={editWeightDialogOpen}
        onClose={() => {
          setEditWeightDialogOpen(false)
          setEditingWeightIndex(null)
        }}
        onSave={handleSaveEditedWeight}
        entry={entry}
        weightIndex={editingWeightIndex}
        isSubmitting={updateEntryMutation.isPending}
      />
    </Container>
  )
}

export default EntryDetailsPage